import { ICommandBarItemProps, SelectionMode } from '@fluentui/react';
import { FileIconType, getFileTypeIconProps } from '@fluentui/react-file-type-icons';
import AppContext from 'App/AppContext';
import {
  IPCommandBar,
  IPIcon,
  IPIconButton,
  IPLink,
  IPScrollablePane,
  IPSpinner,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPDetailsList, IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ModifyTextDialog from 'components/Dialogs/ModifyTextDialog';
import WarningMessage from 'components/Notification/WarningMessage';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapSmall,
  newIcon,
  openExtIcon,
  refreshIcon,
} from 'globalStyles';
import { ProviderContainer, ProviderItem } from 'models/documentConnection';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetConnectionContainers, apiGetConnectionItems } from 'services/Api/documentProviderService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { navigateToExternalUrl } from 'utils/url';
import { getBrokeredErrorMessage, notifyBrokeredError } from './brokeredErrors';
import { ConnectProviderAccountCard } from './ConnectProviderAccountCard';

const iconSize = 20;

export interface IBrokeredBreadcrumb {
  /** What the provider wants to be asked for this folder's children. */
  folderId: string | undefined;
  name: string;
  /** Where this folder lives at the provider, when it told us; for the Open command. */
  webUrl?: string;
}

interface IBrokeredDirectoryExplorerProps {
  connectionId: number;
  /** When set the explorer starts inside this container instead of listing them. */
  containerId?: string;
  /** Start folder inside the container, for a picker bound to a category root. */
  rootFolderId?: string;
  selectionMode?: SelectionMode;
  /**
   * Picking a folder rather than an item. Files stay listed, the same way the SharePoint
   * picker keeps them: hiding them makes a folder that holds documents look empty, and the
   * one thing the user is deciding is whether this is the right folder.
   */
  selectFolderMode?: boolean;
  /** Named in the Open command, so it reads "Open in Google Drive". */
  providerName?: string;
  /** Change this to re-read the folder currently shown, without losing the place in the tree. */
  reloadToken?: number;
  disabled?: boolean;
  /**
   * Creating a folder needs a category, because that is what binds a connection to a place the
   * user may write. A host without one leaves this out and the command is not offered.
   */
  onCreateFolder?: (parentFolderId: string | undefined, name: string) => Promise<void>;
  onSelectItems?: (items: ProviderItem[]) => void;
  /**
   * path is the readable trail, container name first, for a host that shows where it is.
   * canBeRoot is false only at the root of a container a category cannot be bound to; a folder
   * always can.
   */
  onNavigate?: (
    containerId: string | undefined,
    folderId: string | undefined,
    breadcrumb: IBrokeredBreadcrumb[],
    path: string,
    canBeRoot: boolean,
  ) => void;
}

/**
 * One browse UI for every brokered provider, laid out like the SharePoint explorer it sits
 * beside: a command bar, a breadcrumb trail with back, open and refresh, and the grid. The
 * server answers containers and items the same way whatever is behind them, so a Google
 * shared drive, a Nextcloud files tree and a Notion workspace all walk the same path here.
 *
 * A path-addressed provider hands back browsePath on each folder; an id-addressed one does
 * not. Passing back whatever the item gave us keeps that difference out of this component.
 */
export const BrokeredDirectoryExplorer = (props: IBrokeredDirectoryExplorerProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['documentproviders', 'library', 'translation']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [containers, setContainers] = useState<ProviderContainer[]>([]);
  const [items, setItems] = useState<ProviderItem[]>([]);
  const [containerId, setContainerId] = useState<string | undefined>(props.containerId);
  const [breadcrumb, setBreadcrumb] = useState<IBrokeredBreadcrumb[]>([]);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string | undefined>(undefined);
  // why the listing on screen could not be read; takes the grid's place until the next read
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  // the code behind it, when the API gave one: a missing per-user grant is a card, not an error
  const [loadErrorCode, setLoadErrorCode] = useState<ControllerExceptionCodes | undefined>(undefined);
  // bumped by the explorer's own refresh and after it creates a folder, so a reload does not
  // have to travel out to the host and back
  const [reloadCount, setReloadCount] = useState<number>(0);

  const currentFolderId = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].folderId : props.rootFolderId;
  const currentWebUrl = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].webUrl : undefined;
  // One list behind both the grid and the "is this folder empty" test. Reading those from two
  // different arrays is how a folder holding only files rendered as a header with no rows and
  // no empty state at all.
  const visibleItems = items;

  // A refusal the API explained (a switched-off connection, revoked credentials, a throttle)
  // stays on screen where the listing would be, the way the SharePoint explorer keeps its
  // warning: a toast is gone in four seconds, and an empty grid then reads as an empty folder.
  // Anything else is unexpected and keeps the scene-level error with its logging.
  const showLoadError = (err: unknown) => {
    const msg = getBrokeredErrorMessage(err, t);

    if (msg) {
      setLoadError(msg);
      setLoadErrorCode(AppError.getControllerExceptionCode(AppError.fromApiError(err)));
    } else {
      appContext.setError(err);
    }
  };

  const clearLoadError = () => {
    setLoadError(undefined);
    setLoadErrorCode(undefined);
  };

  const loadContainers = useCallback(async () => {
    try {
      setIsLoading(true);
      clearLoadError();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      setContainers(await apiGetConnectionContainers(props.connectionId, undefined, accessToken));
    } catch (err) {
      // a list left over from before the failure would read as "still fine"
      setContainers([]);
      showLoadError(err);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.connectionId]);

  const loadItems = useCallback(
    async (targetContainerId: string, folderId: string | undefined) => {
      try {
        setIsLoading(true);
        clearLoadError();
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const page = await apiGetConnectionItems(props.connectionId, targetContainerId, folderId, undefined, accessToken);
        setItems(page.items);
      } catch (err) {
        setItems([]);
        showLoadError(err);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.connectionId],
  );

  // a different connection has different containers, so the place in the old tree is gone
  useEffect(() => {
    setContainerId(props.containerId);
    setBreadcrumb([]);
    setItems([]);
  }, [props.connectionId, props.containerId]);

  useEffect(() => {
    if (containerId) {
      loadItems(containerId, currentFolderId);
    } else {
      loadContainers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, currentFolderId, props.connectionId, props.reloadToken, reloadCount]);

  // a host that pinned the container never listed it, so an unknown container answers as a place
  const containerCanBeRoot = (forContainerId: string | undefined): boolean =>
    containers.find((c) => c.id === forContainerId)?.canBeRoot ?? true;

  const openContainer = (container: ProviderContainer) => {
    setContainerId(container.id);
    setBreadcrumb([]);
    props.onNavigate?.(container.id, undefined, [], buildPath([], container.id), container.canBeRoot);
  };

  const openFolder = (item: ProviderItem) => {
    // a path-addressed provider walks by path, an id-addressed one by id
    const next = [...breadcrumb, { folderId: item.browsePath ?? item.id, name: item.name, webUrl: item.webUrl }];
    setBreadcrumb(next);
    props.onNavigate?.(containerId, next[next.length - 1].folderId, next, buildPath(next, containerId), true);
  };

  // Walks back to any point in the trail, so a deep folder does not need one click per level.
  // An index of -1 is the container root.
  const goToCrumb = (index: number) => {
    const next = breadcrumb.slice(0, index + 1);
    setBreadcrumb(next);
    props.onNavigate?.(
      containerId,
      next.length > 0 ? next[next.length - 1].folderId : undefined,
      next,
      buildPath(next, containerId),
      next.length > 0 || containerCanBeRoot(containerId),
    );
  };

  const goUp = () => {
    if (breadcrumb.length > 0) {
      goToCrumb(breadcrumb.length - 2);

      return;
    }

    // out of the container and back to the list of them, unless the host pinned one
    if (!props.containerId) {
      setContainerId(undefined);
      props.onNavigate?.(undefined, undefined, [], '', false);
    }
  };

  const canGoUp = breadcrumb.length > 0 || (!!containerId && !props.containerId);

  // built from the trail being moved to rather than from state, because the caller is told
  // where it now is in the same turn that state is set
  const buildPath = (crumbs: IBrokeredBreadcrumb[], forContainerId: string | undefined): string => {
    const names = crumbs.map((b) => b.name);
    const containerName = containers.find((c) => c.id === forContainerId)?.name;
    if (containerName) names.unshift(containerName);

    return names.join(' / ');
  };

  const createFolder = async () => {
    if (!props.onCreateFolder || !newFolderName) return;

    try {
      setIsLoading(true);
      await props.onCreateFolder(currentFolderId, newFolderName);
      setNewFolderName(undefined);
      setShowNewFolderDialog(false);
      // re-read where we are, in place: the new folder belongs in this listing
      setReloadCount((count) => count + 1);
    } catch (err) {
      notifyBrokeredError(appContext, err, t);
    } finally {
      setIsLoading(false);
    }
  };

  const commandButtons: ICommandBarItemProps[] = [];

  if (props.onCreateFolder) {
    commandButtons.push({
      key: 'new-folder',
      text: t('documentproviders:Picker.NewFolder'),
      iconProps: newIcon,
      disabled: props.disabled || isLoading || !containerId || !!loadError,
      onClick: () => setShowNewFolderDialog(true),
    });
  }

  const getBreadcrumbItems = (): IBrokeredBreadcrumb[] => breadcrumb;

  const containerName = containers.find((c) => c.id === containerId)?.name;

  const renderSeparator = (key: string) => (
    <IPText key={key} block nowrap styles={{ root: { flexShrink: 0 } }}>
      {'>'}
    </IPText>
  );

  const renderCrumb = (key: string, text: string, isLast: boolean, onClick: () => void) => (
    <IPText
      block
      nowrap
      key={key}
      title={text}
      styles={{ root: { maxWidth: 180, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' } }}
    >
      {isLast ? text : <IPLink underline onClick={onClick}>{text}</IPLink>}
    </IPText>
  );

  //
  // Main render
  //
  return (
    <IPStack verticalFill>
      {commandButtons.length > 0 && (
        <IPStack.Item>
          <IPCommandBar styles={{ root: { padding: 0, marginBottom: 10 } }} items={commandButtons} />
          <ModifyTextDialog
            isOpen={showNewFolderDialog}
            value={newFolderName}
            required={true}
            title={t('documentproviders:Picker.NewFolder')}
            placeholder={t('documentproviders:Picker.FolderName')}
            maxLength={400}
            onClose={() => {
              setNewFolderName(undefined);
              setShowNewFolderDialog(false);
            }}
            onSave={createFolder}
            onUpdate={(value: string) => setNewFolderName(value)}
          />
        </IPStack.Item>
      )}
      {/* the trail and the open command need a container; refresh is always there, because the
          list of containers is the first thing that fails when a connection is broken */}
      <IPStack.Item>
        <IPStack horizontal horizontalAlign="space-between" verticalAlign="center">
          <IPStack.Item grow styles={{ root: { minWidth: 0, overflow: 'hidden' } }}>
            {containerId && (
              <IPStack
                horizontal
                verticalAlign="center"
                tokens={globalStackTokensGapExtraSmall}
                styles={{ root: { minWidth: 0, maxWidth: '100%', overflow: 'hidden' } }}
              >
                <IPIconButton
                  iconProps={{ iconName: 'NavigateBack' }}
                  title={t('documentproviders:Picker.Back')}
                  disabled={!canGoUp || isLoading}
                  onClick={goUp}
                />
                {containerName &&
                  renderCrumb('container', containerName, breadcrumb.length === 0, () => goToCrumb(-1))}
                {getBreadcrumbItems().map((crumb, index) => (
                  <Fragment key={`${crumb.name}-${index}`}>
                    {renderSeparator(`sep-${index}`)}
                    {renderCrumb(`crumb-${index}`, crumb.name, index === breadcrumb.length - 1, () =>
                      goToCrumb(index),
                    )}
                  </Fragment>
                ))}
              </IPStack>
            )}
          </IPStack.Item>
          <IPStack.Item disableShrink>
            <IPStack horizontal>
              {currentWebUrl && (
                <IPTooltipHost
                  content={t('library:PinnedLinkTabs.Commands.OpenInProvider', {
                    provider: props.providerName ?? '',
                  })}
                >
                  <IPIconButton
                    iconProps={openExtIcon}
                    onClick={() => navigateToExternalUrl(currentWebUrl, undefined, '')}
                  />
                </IPTooltipHost>
              )}
              <IPTooltipHost content={t('translation:General.Button.Refresh')}>
                <IPIconButton
                  iconProps={refreshIcon}
                  disabled={props.disabled || isLoading}
                  onClick={() => setReloadCount((count) => count + 1)}
                />
              </IPTooltipHost>
            </IPStack>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      {isLoading && (
        <IPStack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 120 } }}>
          <IPSpinner size={IPSpinnerSize.medium} />
        </IPStack>
      )}
      {/* the one refusal that is the user's to fix: their own account is not connected yet */}
      {!isLoading && loadError && loadErrorCode === ControllerExceptionCodes.Code_DocumentProvider_UserGrantRequired && (
        <IPStack.Item grow styles={{ root: { minHeight: 150 } }}>
          <ConnectProviderAccountCard
            connectionId={props.connectionId}
            providerName={props.providerName ?? ''}
            message={loadError}
            onConnected={() => setReloadCount((count) => count + 1)}
          />
        </IPStack.Item>
      )}
      {!isLoading && loadError && loadErrorCode !== ControllerExceptionCodes.Code_DocumentProvider_UserGrantRequired && (
        <IPStack.Item grow styles={{ root: { minHeight: 150 } }}>
          <WarningMessage message={loadError} />
        </IPStack.Item>
      )}
      {!isLoading && !loadError && !containerId && (
        <IPStack.Item grow styles={{ root: { minHeight: 150, position: 'relative' } }}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
            <IPDetailsList
              items={containers}
              layoutMode={IPDetailsListLayoutMode.justified}
              selectionMode={SelectionMode.none}
              columns={[
                {
                  key: 'name',
                  name: t('documentproviders:Picker.Containers'),
                  minWidth: 200,
                  isResizable: true,
                  onRender: (item?: ProviderContainer) =>
                    item ? (
                      <IPText>
                        <IPLink onClick={() => openContainer(item)}>{item.name}</IPLink>
                      </IPText>
                    ) : null,
                },
              ]}
            />
          </IPScrollablePane>
        </IPStack.Item>
      )}
      {!isLoading && !loadError && containerId && visibleItems.length === 0 && (
        <IPStack.Item grow styles={{ root: { minHeight: 150 } }}>
          <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
            <IPStack.Item>
              <IPText>{t('translation:General.Notifications.NoItems')}</IPText>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      )}
      {!isLoading && !loadError && containerId && visibleItems.length > 0 && (
        <IPStack.Item grow styles={{ root: { minHeight: 150, position: 'relative' } }}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
            <IPDetailsList
              items={visibleItems}
              layoutMode={IPDetailsListLayoutMode.justified}
              selectionMode={props.selectionMode ?? SelectionMode.none}
              onActiveItemChanged={(item?: ProviderItem) => {
                // a file is listed while picking a folder, but it is not what is being picked
                if (item && !item.isFolder && !props.selectFolderMode) props.onSelectItems?.([item]);
              }}
              columns={[
                {
                  key: 'type',
                  name: '',
                  minWidth: 20,
                  maxWidth: 20,
                  onRender: (item?: ProviderItem) => {
                    if (!item?.name) return null;

                    if (item.isFolder) {
                      return <IPIcon {...getFileTypeIconProps({ size: iconSize, type: FileIconType.folder })} />;
                    }

                    // the same name-based lookup the SharePoint picker uses; a provider's mime
                    // type says nothing Fluent's icon set understands
                    const splits = item.name.split('.');

                    return (
                      <IPIcon {...getFileTypeIconProps({ extension: splits[splits.length - 1], size: iconSize })} />
                    );
                  },
                },
                {
                  key: 'name',
                  name: t('library:DetailList.Header.Name'),
                  minWidth: 240,
                  isResizable: true,
                  onRender: (item?: ProviderItem) => {
                    if (!item) return null;

                    if (item.isFolder) {
                      return (
                        <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
                          <IPText>
                            <IPLink onClick={() => openFolder(item)}>{item.name}</IPLink>
                          </IPText>
                        </IPStack>
                      );
                    }

                    return <IPText>{item.name}</IPText>;
                  },
                },
                {
                  key: 'modified',
                  name: t('library:DetailList.Header.Changed'),
                  minWidth: 140,
                  maxWidth: 200,
                  isResizable: true,
                  onRender: (item?: ProviderItem) =>
                    item?.modified ? <IPText>{item.modified.toLocaleDateString()}</IPText> : null,
                },
              ]}
            />
          </IPScrollablePane>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
